import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { ChevronDown, RotateCw } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { CONVERSATION_PLAYBACK_SPEEDS } from '@duke-hq/shared-constants/conversationPlaybackSpeeds';
import { PlaybackSpeed } from './conversationPlayback';
import { PreviewLanguagePicker } from './PreviewLanguagePicker';
import { testIds } from './testIds';

type PlaybackLanguageControls = {
  value: string;
  onChange: (language: string) => void;
  readyByLanguage?: ReadonlyMap<string, boolean>;
};

// -- ConversationPlaybackControl -----------------------------------------------
// The shared playback controls for the admin conversation previews: an optional
// reading-language picker (mirroring the student switcher), a speed dropdown
// (mirroring the student runtime's selector), an admin-only "skip animation"
// toggle that renders the whole conversation at once, and a restart button that
// replays from the start. Kept admin-only on purpose; students never see an
// instant-render option.
export function ConversationPlaybackControl({
  speed,
  onChange,
  onRestart,
  language,
}: {
  speed: PlaybackSpeed;
  onChange: (speed: PlaybackSpeed) => void;
  onRestart: () => void;
  language?: PlaybackLanguageControls;
}) {
  const isInstant = speed === 'instant';
  return (
    <HStack spacing="2">
      {language ? (
        <PreviewLanguagePicker
          value={language.value}
          onChange={language.onChange}
          readyByLanguage={language.readyByLanguage}
        />
      ) : null}
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          colorScheme="gray"
          rightIcon={<ChevronDown size={iconSize.sm} />}
          isDisabled={isInstant}
          aria-label={t`Speed`}
          data-testid={testIds.speedMenuButton}
        >
          {isInstant ? '—' : `${speed}x`}
        </MenuButton>
        <MenuList minW="fit-content">
          <MenuOptionGroup value={isInstant ? undefined : speed.toString()} type="radio">
            {CONVERSATION_PLAYBACK_SPEEDS.map((value) => (
              <MenuItemOption
                key={value}
                value={value.toString()}
                data-testid={testIds.speedOption}
                onClick={() => onChange(value)}
              >
                {value}x
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
      <IconButton
        size="sm"
        variant="outline"
        colorScheme="gray"
        data-testid={testIds.restartButton}
        aria-label={t`Replay conversation`}
        icon={<RotateCw size={iconSize.sm} />}
        onClick={onRestart}
      />
      <Button
        size="sm"
        data-testid={testIds.skipAnimationButton}
        variant={isInstant ? 'solid' : 'outline'}
        colorScheme="gray"
        isDisabled={isInstant}
        aria-pressed={isInstant}
        onClick={() => onChange('instant')}
      >
        <Trans>Skip animation</Trans>
      </Button>
    </HStack>
  );
}
